<template>
    <Spin :spinning="status===statusMap.LOADING && page == 1">
        <div class="load-more-container">
            <template v-if="$slots.item">
                <div v-for="(item, index) in list">
                    <slot name="item" :item="item" :index="index"></slot>
                </div>
            </template>

            <slot :list="list"></slot>

            <Spin :spinning="status===statusMap.LOADING">

                <div class="load-more">
                    <div v-if="status===statusMap.NO_MORE">
                        <slot name="no-data" v-if="list.length===0">暂无数据</slot>
                        <slot name="no-more" v-else>没有更多数据了</slot>
                    </div>
                    <div v-if="status===statusMap.LOADING">
                        <slot name="loading">数据加载中</slot>
                    </div>
                    <Link v-if="status===statusMap.READY"
                          @click="()=>loadPage()"
                          :href="props.loadMoreUrl"
                          class="cursor-pointer">
                        <slot name="ready" v-if="status===statusMap.READY">点击加载更多</slot>
                    </Link>
                </div>
            </Spin>
        </div>
    </Spin>
</template>

<script lang="ts" setup>
import {nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import {debounce, isArray} from "lodash"
import {Spin} from "ant-design-vue";
import Link from "@/Components/Link.vue";

const statusMap = {
    READY: 1,
    LOADING: 2,
    NO_MORE: 3,
}

const props = withDefaults(defineProps<{
    loadPage?: LoadPageFunction,
    distance?: number,
    page?: number,
    list?: any[],
    loadMoreUrl?: string,
}>(), {
    loadPage: (page) => [],
    distance: 200,
    page: 1,
    list: [] as any,
    loadMoreUrl: 'javascript:;'
})

const status = ref(statusMap.READY)
const list = ref(props.list || [])

const emits = defineEmits(['update:page', 'update:list', 'loadPage', 'clearItems', 'addItems'])

const page = ref<number>(props.page)

const loadPage = debounce(async (reload = false) => {
    try {
        if ([statusMap.LOADING, statusMap.NO_MORE].indexOf(status.value) !== -1 && !reload) {
            return
        }
        if (reload) {
            page.value = 1
            emits('update:page', page.value)
        }
        status.value = statusMap.LOADING
        const resList = await props.loadPage(page.value)
        const scrollTop = window.scrollY
        if (reload) {
            list.value = []
            await nextTick()
            window.scrollTo(0, scrollTop)
            emits('clearItems')
        }
        if (!isArray(resList)) {
            console.error(resList, 'is not a list')
            return
        }

        if (resList.length === 0) {
            status.value = statusMap.NO_MORE
            return
        }

        page.value++
        emits('update:page', page.value)

        list.value = list.value.concat(resList as any) as any
        await emits('addItems', resList)

        status.value = statusMap.READY
    } catch (e) {
        status.value = statusMap.READY
        throw e
    }
}, 500)

watch(() => props.page, () => {
    page.value = props.page
})

watch(() => props.list, () => {
    list.value = props.list
})

watch(list, () => {
    emits('update:list', list.value)
})

const scrollHandler = () => {
    if (status.value == statusMap.NO_MORE) {
        return
    }
    if (document.body.querySelector('#app').clientHeight - (window.scrollY + window.innerHeight) <= props.distance) {
        loadPage()
    }
}

onMounted(() => {
    if (list.value.length <= 0) {
        loadPage()
    }

    window.addEventListener('scroll', scrollHandler)

    scrollHandler()
})

onUnmounted(() => {
    window.removeEventListener('scroll', scrollHandler)
})

defineExpose({
    loadPage,
    reloadPage() {
        loadPage(true)
    }
})

</script>

<style scoped lang="less">
.load-more {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
    color: #333;
}
</style>
